import React from 'react';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import JobErrorTable from 'pages/objectManager/components/JobResults/pages/components/JobErrorTable';
import useErrorTableData, {
  countFailedObjects,
} from 'pages/objectManager/components/JobResults/pages/hooks/useErrorTableData';
import type { TObjectImportJob } from 'types/objectJob.type';
import { Typography } from '@timeedit/ui-components';
import { Space, Spin } from '@timeedit/ui-components/antd';
import { LabelWrapper } from '@timeedit/ui-components/components/InputComponent/Input';
import './ImportResult.scss';

type Props = {
  job: TObjectImportJob;
};

const ImportResult: React.FC<Props> = (props) => {
  const validating = [
    'VALIDATE_QUEUED',
    'VALIDATING',
    'PENDING_REVIEW',
  ].includes(props.job.status);
  const queued = props.job.status === 'QUEUED';
  const running = props.job.status === 'RUNNING';
  const finished = ['FINISHED', 'FAILED'].includes(props.job.status);
  const allErrors = useErrorTableData(props.job);

  const errorData = useMemo(
    () => allErrors.filter((d) => d.kind !== 'VALIDATION'),
    [allErrors]
  );
  const failedCount = useMemo(() => countFailedObjects(errorData), [errorData]);
  const importStarted =
    props.job.summary.successful > 0 ||
    (props.job.unchangedObjects?.length ?? 0) > 0 ||
    allErrors.some((d) => d.kind === 'EXECUTION');

  return (
    <>
      <Typography.Title level={4} className="execution-result-title">
        Import Result
      </Typography.Title>
      <div className="validation-result-box">
        {validating && (
          <Typography.Text>
            Not imported - Click Import Objects to import the objects when the
            file validation is done.
          </Typography.Text>
        )}
        {queued && (
          <Space>
            <Spin />
            <Typography.Text>Import queued</Typography.Text>
          </Space>
        )}
        {running && (
          <Space>
            <Spin />
            <Typography.Text>Import in progress</Typography.Text>
          </Space>
        )}
        {finished && !importStarted && (
          <Typography.Text>
            Not imported - the file did not pass validation, so no objects were
            created or updated. See Validation Result above.
          </Typography.Text>
        )}
        {finished && importStarted && (
          <Space
            orientation="vertical"
            className="import-result-finished-container"
            style={{ width: '100%' }}
          >
            <LabelWrapper label="Started">
              <b>
                {props.job.startedAt &&
                  dayjs(props.job.startedAt).format('YYYY-MM-DD HH:mm:ss')}
              </b>
            </LabelWrapper>
            <LabelWrapper label="Started by">
              <b>{props.job.userNameOrEmail}</b>
            </LabelWrapper>
            <LabelWrapper
              label="Successful object updates"
              tooltip="Objects that were successfully created or updated."
            >
              <b>
                {props.job.summary.successful}/{props.job.summary.total}
              </b>
            </LabelWrapper>
            <LabelWrapper
              label="Unchanged objects"
              tooltip="Objects that already matched the file and needed no update. Counted separately from successful updates, so this figure is part of the total."
            >
              <b>{props.job.summary.unchanged ?? 0}</b>
            </LabelWrapper>
            <LabelWrapper
              label="Issues (failed object updates)"
              tooltip="Objects that reached the import but were not updated, listed below. Rows rejected during file validation never reach the import and are listed under Validation Result above."
            >
              <b>{failedCount}</b>
            </LabelWrapper>
            {errorData.length > 0 && (
              <div style={{ width: '100%' }}>
                <JobErrorTable
                  data={errorData}
                  title="Issues (failed object updates)"
                />
              </div>
            )}
          </Space>
        )}
      </div>
    </>
  );
};

export default ImportResult;
